import React, { FC, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import OrderOnItsWay from '@assets/order_on_its_way.svg';
import { useScroll } from '@src/shared/hooks/use-scroll';
import { useMobileViewActions } from '@src/shared/context/mobile-view';
import useFMFeatureFlag from '@src/shared/hooks/use-fm-featureflag';
import { JETFM_TIPS_RESTRICTED_COUNTRY_LIST } from '@src/shared/models';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import useAnnouncementBannerLogic from '@src/shared/hooks/use-anouncement-banner-logic';
import utilStorage from '@src/shared/services/util-storage';
import styles from './CourierPageHeader.module.scss';
import { IconPlus, IconSettings } from '@justeattakeaway/pie-icons-react';
import PIEText from '@src/shared/components/PIEText';
import PIEButton from '@src/shared/components/PIEButton';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import useOCFStatus from '@src/shared/hooks/use-ocf-status';
import QRCodeOptionsSelector from '@src/shared/components/QRCodeOptionsSelector';
interface ICourierPageHeaderProps {
  onSettings: () => void;
  onShowQRCode: () => void;
  onDownloadQRCode: () => void;
  onLegacyAddCourier: () => void;
  isFunctional?: Boolean;
}

export const CourierPageHeader: FC<ICourierPageHeaderProps> = ({
  onSettings,
  onShowQRCode,
  onDownloadQRCode,
  onLegacyAddCourier,
  isFunctional = true,
}) => {
  const { t } = useTranslation();
  const { isResolutionMobile } = useDeviceDetect();
  const onScrollPosition = useRef<HTMLHeadingElement | null>(null);
  const isDesktopScreen = !isResolutionMobile;
  const { scrollTopNative, scrollDirection } = useScroll();
  const { setMobileHeaderNavigationVisibility } = useMobileViewActions();
  const [isTipsActive, setIsTipsActive] = useState(false);
  const { isOCFEnabled } = useOCFStatus();
  const { finalizeBanner } = useAnnouncementBannerLogic({
    trackingTitle: '',
    onFinalize: () => {
      utilStorage.setManageCourierSettingsTutorialSeen('NO_SHOW');
    },
  });
  const { restaurant } = useKeycloakUserState();
  const tipsRestrictedCountryList = useFMFeatureFlag(JETFM_TIPS_RESTRICTED_COUNTRY_LIST);
  useEffect(() => {
    if (!restaurant || !tipsRestrictedCountryList) {
      return;
    }

    if (!tipsRestrictedCountryList.includes(restaurant?.country)) {
      setIsTipsActive(true);
    }
  }, [restaurant, tipsRestrictedCountryList]);

  useEffect(() => {
    if (onScrollPosition?.current) {
      const scrollStartingPositionUp =
        onScrollPosition?.current?.offsetTop - 56 + onScrollPosition?.current?.clientHeight;
      const scrollStartingPositionDown = onScrollPosition?.current?.clientHeight;
      if (scrollTopNative > scrollStartingPositionUp && scrollDirection === 'up') {
        setMobileHeaderNavigationVisibility(true);
      } else if (scrollTopNative <= scrollStartingPositionDown && scrollDirection === 'down') {
        setMobileHeaderNavigationVisibility(false);
      }
    }
  }, [scrollTopNative]);

  return (
    <div className={styles.courierPageHeaderContainer}>
      <div
        className={`${styles.courierPageHeaderContent} ${!isFunctional ? 'non-functional' : ''}`}
      >
        <PIEText
          text={<Trans i18nKey="navigation.couriers" />}
          size={'var(--xds-font-size-32)'}
          fontWeight="var(--xds-font-heading-xxl-weight)"
        />

        {isFunctional && (
          <div className={styles.courierPageHeaderButtons}>
            {isOCFEnabled ? (
              <QRCodeOptionsSelector
                onShowQRCode={onShowQRCode}
                onDownloadQRCode={onDownloadQRCode}
                qaSelector="courier-page-add-courier-button"
                renderToggle={({ onClick }) => (
                  <PIEButton
                    onClick={onClick}
                    className={styles.addCourierButton}
                    text={t('manage_couriers.ADD_COURIER_BUTTON_ALTERNATE')}
                    size="small"
                    icon={<IconPlus size="s" />}
                    mode="primary"
                    type="button"
                    qaSelector="courier-page-add-courier-button"
                  />
                )}
              />
            ) : (
              <PIEButton
                onClick={onLegacyAddCourier}
                className={styles.addCourierButton}
                text={t('manage_couriers.ADD_COURIER_BUTTON')}
                size="small"
                icon={<IconPlus size="s" />}
                mode="primary"
                type="button"
                qaSelector="courier-page-add-courier-button"
              />
            )}

            {isTipsActive && isOCFEnabled && isResolutionMobile ? (
              <div style={{ marginLeft: '-4px', marginTop: '16px', width: '40px' }}>
                <PIEButton
                  id="manage-courier-tip-settings-button"
                  onClick={() => {
                    onSettings();
                  }}
                  className={styles.iconButton}
                  aria-label="settings"
                  icon={<IconSettings color={'var(--xds-color-interactive-brand)'} size="m" />}
                  text={''}
                  size={'small'}
                  mode={'secondary'}
                  type={'button'}
                  autoWidth
                />
              </div>
            ) : null}

            {isTipsActive && isDesktopScreen ? (
              <PIEButton
                id="manage-courier-tip-settings-button"
                onClick={() => {
                  onSettings();
                  finalizeBanner();
                }}
                icon={<IconSettings color={'var(--xds-color-interactive-brand)'} size={'s'} />}
                size={'medium'}
                mode="secondary"
                type="submit"
                className={styles.settingsButton}
                text={t('tips.SETTINGS_BUTTON')}
              />
            ) : null}
          </div>
        )}
      </div>
      {isFunctional && !isOCFEnabled && (
        <div className={styles.courierPageHeaderLogo}>
          <img src={OrderOnItsWay} alt="On Its Way" />
        </div>
      )}
    </div>
  );
};

export default CourierPageHeader;
